import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { ApiError, apiClient } from '@/services/apiClient'
import { RESTRICTED_TASK } from '@/components/process/restrictedText'
import type { RecordBindingRead, RecordProcessRead, TaskListRead, TaskRead, TaskScope } from '@/types/process'

export const processKey = ['process'] as const

export function useProcessTasks(scope: TaskScope, status: 'open' | 'done' = 'open'): UseQueryResult<TaskListRead, Error> {
  return useQuery({
    queryKey: [...processKey, 'tasks', scope, status],
    queryFn: () => apiClient.get<TaskListRead>(`/api/process/tasks?scope=${scope}&status=${status}`),
    staleTime: 15_000,
  })
}

/** SA-1032: `staleTime: 0` — a write on the record page (a quote recorded as
 * sent, a response) can end this task without touching this key, and Back
 * must not serve the cached open task. One row, read on mount. */
export function useProcessTask(taskId: string | undefined): UseQueryResult<TaskRead, Error> {
  return useQuery({
    queryKey: [...processKey, 'task', taskId ?? ''],
    enabled: !!taskId,
    staleTime: 0,
    queryFn: () => apiClient.get<TaskRead>(`/api/process/tasks/${taskId}`),
  })
}

/** E5 (spec §4.3): a record's own task history — every kind, newest first
 * when `done`. Disabled until `orderId` is set, so a disclosure can fetch
 * on open. */
export function useRecordTasks(
  orderId: string | undefined, status: 'open' | 'done',
): UseQueryResult<TaskListRead, Error> {
  return useQuery({
    queryKey: [...processKey, 'record-tasks', orderId ?? '', status],
    enabled: !!orderId,
    queryFn: () => apiClient.get<TaskListRead>(`/api/process/records/${orderId}/tasks?status=${status}`),
  })
}

function isNotFound(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { status?: number }).status === 404
}

function isConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409
}

// E5 (spec §6.2): the re-open refusals arrive as 409 `{code, message}`. A Map,
// not an object literal, so an inherited key (`constructor`) never reads as words.
const CODE_TEXT = new Map<string, string>(Object.entries({
  process_closed: 'This record is closed, and a closed record is not re-opened. Re-doing the quote starts a new process.',
  reopen_entry_refused: 'The intake step is not re-opened. Change the intake fields on the record instead.',
  task_not_done: 'Only a completed task can be re-opened.',
}))

/**
 * Turns a process-API error into a message safe to show a user.
 *
 * Backend contract (SA-376): string `detail` on a rejected command,
 * or a structured object — `{already_completed}` (complete replay),
 * `{claimed_by, status}` (claim conflict), `{instance_id, status}`
 * (start conflict), `{code: "restricted_fields", fields}` (the E5 work
 * gate), `{code}` (E5 re-open refusals, 409) — that needs its own copy
 * since `err.message` is just "<METHOD> <path> failed: <statusText>".
 * A bare code is never printed.
 */
export function processErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (typeof err.detail === 'string') return err.detail
    if (err.detail && typeof err.detail === 'object') {
      const detail = err.detail as Record<string, unknown>
      if ('already_completed' in detail) return 'This task was already completed.'
      if ('claimed_by' in detail) return 'Someone else holds this task.'
      if ('instance_id' in detail) return 'A process is already running on this record.'
      // E5 (spec §5.4): the work gate's 403 — worded, never the bare code.
      if (detail.code === 'restricted_fields') return RESTRICTED_TASK
      const coded = typeof detail.code === 'string' ? CODE_TEXT.get(detail.code) : undefined
      if (coded) return coded
    }
  }
  return err instanceof Error ? err.message : String(err)
}

/** `options.staleTime` is the observer's own (SA-1032: the task page's status
 * line reads afresh on mount); the band keeps the app default. */
export function useRecordProcess(
  orderId: string | undefined, options: { staleTime?: number } = {},
): UseQueryResult<RecordProcessRead | null, Error> {
  return useQuery({
    queryKey: [...processKey, 'record', orderId ?? ''],
    enabled: !!orderId,
    ...options,
    queryFn: async () => {
      try {
        return await apiClient.get<RecordProcessRead>(`/api/process/records/${orderId}`)
      } catch (err) {
        if (isNotFound(err)) return null
        throw err
      }
    },
  })
}

/** SA-469 (spec D6): the process Start would start on this record; null on a
 * 404 (nothing binds). Asked only while the record has no process. */
export function useRecordBinding(orderId: string | undefined, enabled: boolean): UseQueryResult<RecordBindingRead | null, Error> {
  return useQuery({
    queryKey: [...processKey, 'record', orderId ?? '', 'binding'],
    enabled: !!orderId && enabled,
    queryFn: async () => {
      try {
        return await apiClient.get<RecordBindingRead>(`/api/process/records/${orderId}/binding`)
      } catch (err) {
        if (isNotFound(err)) return null
        throw err
      }
    },
  })
}

function useInvalidating<TArgs, TOut>(fn: (args: TArgs) => Promise<TOut>) {
  const qc = useQueryClient()
  return useMutation<TOut, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: processKey })
    },
    onError: (err) => {
      if (isConflict(err)) void qc.invalidateQueries({ queryKey: processKey })
    },
  })
}

export const useClaimTask = () =>
  useInvalidating<{ taskId: string }, TaskRead>(({ taskId }) => apiClient.post(`/api/process/tasks/${taskId}/claim`, {}))

export const useReleaseTask = () =>
  useInvalidating<{ taskId: string }, TaskRead>(({ taskId }) => apiClient.post(`/api/process/tasks/${taskId}/release`, {}))

export const useSaveTaskForm = () =>
  useInvalidating<{ taskId: string; fields: Record<string, unknown> }, TaskRead>(({ taskId, fields }) =>
    apiClient.put(`/api/process/tasks/${taskId}/form`, { fields }),
  )

export const useCompleteTask = () =>
  useInvalidating<{ taskId: string; exit: string; fields: Record<string, unknown> }, RecordProcessRead>(
    ({ taskId, exit, fields }) => apiClient.post(`/api/process/tasks/${taskId}/complete`, { exit, fields }),
  )

export const useReopenTask = () =>
  useInvalidating<{ taskId: string; reason: string }, RecordProcessRead>(({ taskId, reason }) =>
    apiClient.post(`/api/process/tasks/${taskId}/reopen`, { reason }),
  )

export const useStartRecordProcess = () =>
  useInvalidating<{ orderId: string }, RecordProcessRead>(({ orderId }) =>
    apiClient.post(`/api/process/records/${orderId}/start`, {}),
  )
